import React, { useState, useEffect } from 'react';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  RefreshCw,
  Filter,
  Download,
  Edit,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useTransactionSync } from '../../hooks/useTransactionSync';

const TransactionControl = () => {
  const { toast } = useToast();
  const { syncTrigger, triggerSync } = useTransactionSync();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    period: 'all'
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustment, setAdjustment] = useState({
    type: 'profit',
    amount: '',
    currency: 'USDT',
    reason: ''
  });

  const BACKEND_URL = '';

  useEffect(() => {
    fetchData();
  }, [filters]);

  // Listen for sync events from other components (User Management)
  useEffect(() => {
    if (syncTrigger > 0) {
      console.log('🔄 TransactionControl: Syncing due to external change');
      fetchData();
    }
  }, [syncTrigger]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('secure_admin_token') || localStorage.getItem('admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      // Fetch statistics
      const statsRes = await fetch(
        `${BACKEND_URL}/api/admin/transactions/transactions/statistics?period=${filters.period}`,
        { headers }
      );
      const statsData = await statsRes.json();
      setStatistics(statsData.statistics);

      // Fetch pending transactions
      const pendingRes = await fetch(
        `${BACKEND_URL}/api/admin/transactions/transactions/pending`,
        { headers }
      );
      const pendingData = await pendingRes.json();
      setPendingTransactions(pendingData.transactions || []);

      // Fetch all transactions
      let query = `limit=50&offset=0`;
      if (filters.type !== 'all') query += `&transaction_type=${filters.type}`;
      if (filters.status !== 'all') query += `&status=${filters.status}`;

      const txnRes = await fetch(
        `${BACKEND_URL}/api/admin/transactions/transactions/all?${query}`,
        { headers }
      );
      const txnData = await txnRes.json();
      setTransactions(txnData.transactions || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transaction data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (transaction, action) => {
    setSelectedTransaction(transaction);
    setActionType(action);
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    try {
      const headers = getAuthHeaders();
      const endpoint = actionType === 'approve' ? 'approve' : 'reject';

      const response = await fetch(
        `${BACKEND_URL}/api/admin/transactions/transactions/${selectedTransaction._id}/${endpoint}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ action: actionType, admin_note: adminNote })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: `Transaction ${actionType}d successfully`
        });
        
        // Trigger sync to update other components
        triggerSync('transaction_control', {
          type: 'transaction_status_change',
          transactionId: selectedTransaction._id,
          status: actionType === 'approve' ? 'completed' : 'rejected',
          userId: selectedTransaction.user_id
        });
        
        setShowActionModal(false);
        setAdminNote('');
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${actionType} transaction`,
        variant: 'destructive'
      });
    }
  };

  const handleBalanceAdjustment = async () => {
    try {
      const headers = getAuthHeaders();

      const response = await fetch(
        `${BACKEND_URL}/api/admin/transactions/transactions/adjust-balance/${selectedUser.user_id}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            adjustment_type: adjustment.type,
            amount: parseFloat(adjustment.amount),
            currency: adjustment.currency,
            reason: adjustment.reason
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: `Balance adjusted: ${adjustment.type} ${adjustment.amount} ${(adjustment.currency || '').toUpperCase()}`
        });
        
        // Trigger sync to update User Management and Trade Analytics
        triggerSync('transaction_control', {
          type: 'balance_adjustment',
          userId: selectedUser.user_id,
          adjustmentType: adjustment.type,
          amount: adjustment.amount,
          currency: adjustment.currency,
          newBalance: data.new_balance
        });
        
        setShowAdjustmentModal(false);
        setAdjustment({ type: 'profit', amount: '', currency: 'USDT', reason: '' });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to adjust balance',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500',
      completed: 'bg-green-500/20 text-green-400 border-green-500',
      rejected: 'bg-red-500/20 text-red-400 border-red-500',
      cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500'
    };

    const icons = {
      pending: Clock,
      completed: CheckCircle,
      rejected: XCircle,
      cancelled: XCircle
    };

    const Icon = icons[status] || Clock;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const icons = {
      deposit: ArrowDownCircle,
      withdrawal: ArrowUpCircle,
      profit: TrendingUp,
      loss: TrendingDown,
      bonus: DollarSign,
      penalty: AlertTriangle
    };
    return icons[type] || DollarSign;
  };

  if (loading && !statistics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-400 font-semibold">Loading Transaction Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-cyan-400" />
            Transaction Control Center
          </h1>
          <p className="text-cyan-400">Monitor and manage all platform transactions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-all"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/40 border border-blue-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-blue-400 text-sm font-semibold">Total Transactions</span>
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{statistics.total_transactions}</p>
            <p className="text-xs text-blue-300 mt-1">{filters.period === 'today' ? 'Today' : filters.period}</p>
          </div>

          <div className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/40 border border-yellow-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-yellow-400 text-sm font-semibold">Pending Approval</span>
              <Clock className="w-5 h-5 text-yellow-400 animate-pulse" />
            </div>
            <p className="text-3xl font-bold text-white">{statistics.pending}</p>
            <p className="text-xs text-yellow-300 mt-1">Requires action</p>
          </div>

          <div className="bg-gradient-to-br from-green-900/40 to-green-800/40 border border-green-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-400 text-sm font-semibold">Completed</span>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{statistics.completed}</p>
            <p className="text-xs text-green-300 mt-1">Successfully processed</p>
          </div>

          <div className="bg-gradient-to-br from-red-900/40 to-red-800/40 border border-red-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-red-400 text-sm font-semibold">Rejected</span>
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-3xl font-bold text-white">{statistics.rejected}</p>
            <p className="text-xs text-red-300 mt-1">Declined transactions</p>
          </div>
        </div>
      )}

      {/* Volume Statistics */}
      {statistics && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Transaction Volume
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-cyan-400 mb-1">Deposits</p>
              <p className="text-xl font-bold text-green-400">
                ${statistics.total_volume.deposits.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-cyan-400 mb-1">Withdrawals</p>
              <p className="text-xl font-bold text-red-400">
                ${statistics.total_volume.withdrawals.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-cyan-400 mb-1">Trades</p>
              <p className="text-xl font-bold text-blue-400">
                ${statistics.total_volume.trades.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-cyan-400 mb-1">Profits</p>
              <p className="text-xl font-bold text-green-400">
                ${statistics.total_volume.profits.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-cyan-400 mb-1">Losses</p>
              <p className="text-xl font-bold text-red-400">
                ${statistics.total_volume.losses.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Filter className="w-5 h-5 text-cyan-400" />
          
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
          >
            <option value="all">All Types</option>
            <option value="deposit">Deposits</option>
            <option value="withdrawal">Withdrawals</option>
            <option value="profit">Profits</option>
            <option value="loss">Losses</option>
            <option value="bonus">Bonuses</option>
            <option value="penalty">Penalties</option>
            <option value="conversion">Conversions</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filters.period}
            onChange={(e) => setFilters({ ...filters, period: e.target.value })}
            className="px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 border-b border-cyan-500/30 pb-2">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
            activeTab === 'all'
              ? 'bg-cyan-500 text-white'
              : 'bg-slate-800/50 text-cyan-400 hover:bg-slate-700'
          }`}
        >
          All Transactions ({transactions.length})
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-t-lg font-semibold transition-all ${
            activeTab === 'pending'
              ? 'bg-yellow-500 text-white'
              : 'bg-slate-800/50 text-yellow-400 hover:bg-slate-700'
          }`}
        >
          Pending Approval ({pendingTransactions.length})
        </button>
      </div>

      {/* Transactions Table */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">User</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-cyan-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cyan-500/10">
              {(activeTab === 'all' ? transactions : pendingTransactions).map((txn) => {
                const TypeIcon = getTypeIcon(txn.type);
                return (
                  <tr key={txn._id} className="hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-semibold">{txn.username}</p>
                        <p className="text-xs text-cyan-400">{txn.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="w-4 h-4 text-cyan-400" />
                        <span className="text-white capitalize">{txn.type}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white font-semibold">
                        {txn.amount} {(txn.currency || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(txn.status)}</td>
                    <td className="px-4 py-3 text-white text-sm">
                      {new Date(txn.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {txn.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAction(txn, 'approve')}
                              className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-all"
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAction(txn, 'reject')}
                              className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            setSelectedUser(txn);
                            setShowAdjustmentModal(true);
                          }}
                          className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-all"
                          title="Adjust Balance"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4 capitalize">
              {actionType} Transaction
            </h3>
            <p className="text-cyan-400 mb-4">
              Are you sure you want to {actionType} this transaction?
            </p>
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
              <p className="text-white"><strong>User:</strong> {selectedTransaction?.username}</p>
              <p className="text-white"><strong>Amount:</strong> {selectedTransaction?.amount} {(selectedTransaction?.currency || '').toUpperCase()}</p>
              <p className="text-white"><strong>Type:</strong> {selectedTransaction?.type}</p>
            </div>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Admin note (optional)"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={confirmAction}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                  actionType === 'approve'
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-red-500 hover:bg-red-600'
                } text-white`}
              >
                Confirm {actionType}
              </button>
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setAdminNote('');
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Adjustment Modal */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-cyan-500 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Balance Adjustment</h3>
            <p className="text-cyan-400 mb-4">
              Adjust balance for: <strong>{selectedUser?.username}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Adjustment Type
                </label>
                <select
                  value={adjustment.type}
                  onChange={(e) => setAdjustment({ ...adjustment, type: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                >
                  <option value="profit">Profit (Add)</option>
                  <option value="loss">Loss (Subtract)</option>
                  <option value="bonus">Bonus (Add)</option>
                  <option value="penalty">Penalty (Subtract)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-cyan-400 text-sm font-semibold mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={adjustment.amount}
                    onChange={(e) => setAdjustment({ ...adjustment, amount: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-cyan-400 text-sm font-semibold mb-2">
                    Currency
                  </label>
                  <select
                    value={adjustment.currency}
                    onChange={(e) => setAdjustment({ ...adjustment, currency: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  >
                    <option value="USDT">USDT</option>
                    <option value="BTC">BTC</option>
                    <option value="ETH">ETH</option>
                    <option value="SOL">SOL</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-cyan-400 text-sm font-semibold mb-2">
                  Reason
                </label>
                <textarea
                  value={adjustment.reason}
                  onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  rows={3}
                  placeholder="Enter reason for adjustment"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleBalanceAdjustment}
                className="flex-1 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
              >
                Apply Adjustment
              </button>
              <button
                onClick={() => {
                  setShowAdjustmentModal(false);
                  setAdjustment({ type: 'profit', amount: '', currency: 'USDT', reason: '' });
                }}
                className="flex-1 px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionControl;
